import { isAiUserType } from '@/lib/auth/restricted-viewer'

/**
 * Sessão por aba, só para usuário de IA (docs/ai-user.md, "Sessão por aba para usuário de IA").
 *
 * A sessão normal é uma por navegador: identidade no `localStorage` e refresh no cookie
 * padrão, compartilhados por todas as abas. O usuário de IA trabalha numa aba, ao lado da
 * sessão de quem o supervisiona, e pode haver vários: a aba dele guarda a identidade no
 * `sessionStorage` (é da aba e sobrevive ao recarregar) e o backend guarda o refresh num
 * cookie só daquela aba. Nada na tela de login: decide o `userType`.
 */

/** Id da aba: vai em toda chamada, e o backend usa só para usuário de IA. */
export const AUTH_TAB_HEADER = 'X-Auth-Tab'
/** A aba é de usuário de IA: renovar e sair pelo cookie dela, nunca pelo padrão. */
export const AUTH_TAB_SCOPE_HEADER = 'X-Auth-Tab-Scope'
export const AUTH_TAB_SCOPE = 'tab'

const TAB_ID_KEY = 'mediardex_tab_id'
const TAB_SESSION_KEY = 'mediardex_tab_session'

function safeSessionStorage(): Storage | null {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null
    } catch {
        return null
    }
}

function newTabId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    // Navegador sem `randomUUID` (contexto não seguro): mesmo formato, para o backend aceitar.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
        const random = (Math.random() * 16) | 0
        return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16)
    })
}

export function getAuthTabId(): string | null {
    const storage = safeSessionStorage()
    if (!storage) return null
    let id = storage.getItem(TAB_ID_KEY)
    if (!id) {
        id = newTabId()
        storage.setItem(TAB_ID_KEY, id)
    }
    return id
}

/** Troca o id da aba (cópia de outra pelo "Duplicar aba"). */
export function renewAuthTabId(): string | null {
    const storage = safeSessionStorage()
    if (!storage) return null
    const id = newTabId()
    storage.setItem(TAB_ID_KEY, id)
    return id
}

export function isTabSession(): boolean {
    return safeSessionStorage()?.getItem(TAB_SESSION_KEY) === '1'
}

/** Marca (ou desmarca) a aba como sessão de usuário de IA, pelo `userType` de quem entrou. */
export function markTabSessionFor(userType: string | null | undefined): void {
    const storage = safeSessionStorage()
    if (!storage) return
    if (isAiUserType(userType)) storage.setItem(TAB_SESSION_KEY, '1')
    else storage.removeItem(TAB_SESSION_KEY)
}

export function clearTabSessionMark(): void {
    safeSessionStorage()?.removeItem(TAB_SESSION_KEY)
}

/** Onde a identidade da sessão desta aba fica guardada. */
export function authStorage(): Storage {
    const session = safeSessionStorage()
    return isTabSession() && session ? session : window.localStorage
}

/** Cabeçalhos de escopo para as chamadas (login, renovação, sair). */
export function authTabHeaders(): Record<string, string> {
    const id = getAuthTabId()
    if (!id) return {}
    return isTabSession()
        ? { [AUTH_TAB_HEADER]: id, [AUTH_TAB_SCOPE_HEADER]: AUTH_TAB_SCOPE }
        : { [AUTH_TAB_HEADER]: id }
}
